<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">GL GROUP</h1>
    <span class="banner py-3 mb-4">
      <div>The GL Accounts page is where each GL account that was included in your budget template are listed by Budget Group and where all the budgeted amounts are entered for the year.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Historical Information</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>For each GL account, the actual amounts from prior years may be displayed below the GL Account as reference. You will have options to view 1-year or 2-years of historical information.</div>
          <div>Beneath each GL Account will appear the historical year line(s) selected. To the left of each historical row will be an expand option that will provide you with details.</div>
          <ul>
            <li>If the transaction originated from an invoice, the row will display that Vendor's name. All transactions for that Vendor will be summarized on that line.</li>
            <li>If the transaction originated from a Journal Entry or New Cash JE, the row will have the title of Journal Entry.</li>
            <li>Other transactions will be labeled Other.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>The Calculator</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>The Calculator icon can be used to help populate the budget amounts for each GL Account, working row or group of GL Accounts. Various calculation formula's may be selected. As you select a formula, the options available for that calculation will be presented to you.</div>
          <div>There are six types of calculations or formulas. Each one is based on a different set of criteria allowing you a wide range of flexibility.</div>
          <div>Calculations will be stored once created, even if you have changed the numbers since the calculation was created or added another calculation. You will have an option on the GL Page to view the two most recent calculations that were performed on that GL Account.</div>
          <div>Calculations will also be displayed when entering your GL Assumption statement as it may be helpful to you in creating the Assumption wording.</div>
          <div>These are the six types of calculations:</div>
          <div><strong>Budgeted GL Account</strong></div>
          <ul>
            <li>Allows you to take another GL Account's existing current year budget and multiply it by a percentage.</li>
            <li>Example: I want my Bad Debt to be 2% of my Gross Potential Rent</li>
            <li>Formula: Select the correct GL Account multiplied by 2%</li>
          </ul>
          <div><strong>Cloning existing Historical row(s)</strong></div>
          <ul>
            <li>Allows you to take the historical vendor rows and use them to create new working rows with the same vendor name and same dollar amounts or make a calculation based on those historical dollar amounts.</li>
            <li>Example: I want the historical vendor working rows to be created and increased by 10%</li>
            <li>Formula: Historical Vendor rows with additional calculations multiplied by 10%.</li>
          </ul>
          <div><strong># of Budgeted Occupied Units, Vacant Units, Total Units, Move Ins, Move Outs, Renewals</strong></div>
          <ul>
            <li>Allows you to take the number budgeted for the item selected multiplied by an amount multiplied by a percentage</li>
            <li>Here are where these numbers are pulling from:</li>
            <li class="no-bullet">
              <ul>
                <li>Occupied Units - Occupancy > Top Summary Section > Ending Occupied Units</li>
                <li>Vacant Units - Occupancy > Top Summary Section > Ending Vacant Units</li>
                <li>Total Units - Occupancy > Top Summary Section > Total Units</li>
                <li>Move-Ins - Occupancy > Move Ins/Outs tab > Top Summary Section > Total Move Ins</li>
                <li>Move-Outs - Occupancy > Move Ins/Outs tab > Top Summary Section > Total Move Outs</li>
                <li>Renewals - Occupancy > Move Ins/Outs tab > Top Summary Section > Projected # Renewed</li>
              </ul>
            </li>
            <li>Example: Pet Fees: I want to charge my move ins $100 for pet owners, but only 50% of my residents own a pet.</li>
            <li>Formula: Move In's x $100 x 50%</li>
            <br/>
            <li>Example 2: Renewal Fees: I want to charge $65 for every renewal</li>
            <li>Formula: Renewals x $65 x 100%</li>
          </ul>
          <div><strong>Starting with nothing, current year budget, or prior year actuals and increase/decrease it by an amount or percentage</strong></div>
          <ul>
            <li>Example: I want to populate every row with $250</li>
            <li>Formula: Starting with nothing, increase by $250</li>
            <br/>
            <li>Example: I want to take last year's actuals and increase them by 4%</li>
            <li>Formula: Starting with Last Year Actuals - Monthly increased by 4%</li>
          </ul>
          <div><strong>Management Fee</strong></div>
          <ul>
            <li>Allows you to use your Management Fee calculation. You will only use this calculation type for Management Fee expense.</li>
            <li>
              You will only be able to select a Management fee if these two items are true:
              <ol>
                <li>The calculation method is entirely based on Account Type = GL Account</li>
                <li>The calculation method accounting basis matches your budget accounting basis</li>
              </ol>
            </li>
            <br />
            <li>Example: I want to retrieve a management fee that can be applied to this row</li>
            <li>Formula: Select the Management Fee</li>
            <li class="no-bullet">
              <ul>
                <li>The system will look at the budgeted amounts for each GL Account set up in Management Fees and apply the percentage.</li>
              </ul>
            </li>
          </ul>
          <div><strong>Turnover Costs</strong></div>
          <ul>
            <li>Allows you to calculation your "turn cost" which is your cost for this GL Account every time someone moves out.</li>
            <li>There are two ways to calculate this number:</li>
            <li>Based on prior year actuals divided by the number of move outs in the prior year.</li>
            <li class="no-bullet">
              <ul>
                <li>
                  If your prior year expense was $12,000 and there were 12 move outs last year, than your turn cost would be $1,000.
                </li>
              </ul>
            </li>
            <li><strong>Based on your manually entered number.</strong> If you know how much this turn cost is, you can enter the amount.</li>
            <li>Once you have your turn cost, the formula will take that amount and multiply it times each month's estimated number of move outs.</li>
            <li>Example: I want to calculate the cost of a turn by looking at my Prior Year expenses annualized.</li>
            <li>Formula: (PY Annual Expenses divided by PY Move outs) multiplied by the number of move outs.</li>
            <li class="no-bullet">
              <ul>
                <li>In the above example ($1,000) multiplied by the number of move outs in each month.</li>
              </ul>
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Working Rows</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Working rows are additional detail rows that add up to the summary row. They allow you to do separate calculations or estimates for items and roll up into the Budget line.</div>
          <div>For example, you may be working on your Marketing expense GL, and would like to create a working row for each one of your sources such as Apartments.com and Zillow. You will be able to either manually add numbers to each row or use the calculator.</div>
          <div><strong>Adding Working Rows</strong></div>
          <ul>
            <li>There are two ways to add additional working rows</li>
            <li><strong>Use the Calculator icon and choose "Cloning Historical Rows."</strong></li>
            <li class="no-bullet">
              <ul>
                <li>This option will create 1 new row for every historical row for last year.</li>
                <li>It will offer you an option to add a formula to the historical data</li>
                <li>Example: Increase those numbers by 10%.</li>
              </ul>
            </li>
            <li><strong>Use the ellipsis (3 dots) on the right of the GL Account and select "Add Row"</strong></li>
            <li>If working rows are added to a GL Account for the first time:</li>
            <li class="no-bullet">
              <ul>
                <li>Any numbers in the budget row will be cleared to zero</li>
                <li>The budget row will not be editable any longer and will become greyed out and a sum of any working rows added</li>
              </ul>
            </li>
            <li>If working rows are added and existing working rows exist:</li>
            <li class="no-bullet">
              <ul>
                <li>A new blank row will be created</li>
                <li>Existing working or summary rows will not be changed</li>
              </ul>
            </li>
            <li>The Calculator may be used on a Summary row if no working rows exist</li>
            <li>If working rows exist, the Calculator may be used on any working row or for the entire GL Account if you would like your formula to be calculated on all the working rows.</li>
          </ul>
          <div><strong>Deleting Working Rows</strong></div>
          <ul>
            <li>If you decide that you do not need a working row, click on the right-side ellipsis (3 dots) and choose Delete Row Options.</li>
            <li>You will be presented with a list of the working rows for this GL account.</li>
            <li>You will be able to delete one or more of the working rows.</li>
            <li>Nothing is saved when a working row is deleted.</li>
            <li>If you remove the last working row, the Budget line will become editable again.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Assumptions</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <ul>
            <li>The assumption statement is a high-level statement that can be viewed on the GL Account or Working Draft pages to explain or justify the budget numbers.</li>
            <li>These statements can also be exported with each GL account.</li>
            <li>Assumptions may be edited in numerous locations.</li>
            <li class="no-bullet">
              <ul>
                <li>From the GL Page, you can click the Assumptions icon, or within the ellipsis menu select Assumptions, or select to view the Assumptions and click into the Assumption sentence.</li>
                <li>You can also edit these from the Working Draft page.</li>
              </ul>
            </li>
            <li>Once you click to edit, a new popup box will appear that displays the two most recent calculations for that GL Account (if any exist) on the left-side.</li>
            <li>The right side is a text box displaying the current assumption. Click into the box to edit the text.</li>
            <li>BudgetsPro does not store any historical information about Assumptions.</li>
            <li class="no-bullet">
              <ul>
                <li>If the existing statement is overwritten, the old text will no longer be available</li>
              </ul>
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(4)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(4)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Summary View</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <ul>
            <li>This view provides a comparison of your budget to last year actual numbers in a column format.</li>
            <li><strong>Per Dollar:</strong></li>
            <li class="no-bullet">
              <ul>
                <li>Current Year Budget: This is the sum of all of the specific GL Account numbers budgeted for this month</li>
                <li>Prior Year Actuals: This is the sum of the actual historical numbers for the specific GL Account numbers</li>
                <li>Var $</li>
                <li class="no-bullet">
                  <ul>
                    <li>If the GL Account type is Income, Other Income or any balance sheet account: Current Year Budget less Prior Year Actuals</li>
                    <li>For other GL Account types, follow the rule set in Admin > Settings > Reports: "Show over budget expense accounts as a negative variance."</li>
                  </ul>
                </li>
                <li>Var % (1 Decimal place, percent sign) is Variance $ divided by Current Year Budget</li>
              </ul>
            </li>
            <li><strong>Per Unit:</strong></li>
            <li class="no-bullet">
              <ul>
                <li>Curr Yr Budget divided by the total units</li>
                <li>PY Actuals divided by the total units</li>
                <li>Variance $</li>
                <li class="no-bullet">
                  <ul>
                    <li>If the GL Account type is Income or Other Income: Current Year Budget less Prior Year Actuals</li>
                    <li>For other GL Account types, follow the rule set in Admin > Settings > Reports: "Show over budget expense accounts as a negative variance."</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li><strong>Per Square Foot:</strong></li>
            <li class="no-bullet">
              <ul>
                <li>Curr Yr Budget divided by the total property square footage</li>
                <li>PY Actuals divided by the total property square footage</li>
                <li>Variance $</li>
                <li class="no-bullet">
                  <ul>
                    <li>If the GL Account type is Income or Other Income: Current Year Budget less Prior Year Actuals</li>
                    <li>For other GL Account types, follow the rule set in Admin > Settings > Reports: "Show over budget expense accounts as a negative variance."</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(5)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(5)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Right-Side Icons & Ellipsis Menu Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>To the right of each GL Account you will have additional options.</div>
          <img src="../../assets/help/gl1.png" />
          <div>
            <img class="small" src="../../assets/help/gl2.png" />
            <span>This icon will open the <strong>Comments and Flags</strong> screen which will allow you to add a general comment, create a flagged comment, or reply to an existing comment. If it is white, no comments have been added.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/gl3.png" />
            <span>If it has changed to solid blue, someone has entered a comment.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/gl4.png" />
            <span>If it has changed to an orange flag, then an unresolved flag or issue exists. Someone will need to click into the Comments and Flags screen and select Resolve the outstanding issue. Once resolved, the icon will change back to blue.</span>
          </div>
          <v-divider></v-divider>
          <div>
            <img class="small" src="../../assets/help/gl5.png" />
            <span>
              Will bring up a screen displaying the most recent calculations that have been performed on this GL Account on the left side.
              <br/>
              The right side of the screen will offer a text box where you can add or edit your Assumption Statement.
              <br/>
              If this icon is white, no assumption information has been added.
            </span>
          </div>
          <div>
            <img class="small" src="../../assets/help/gl6.png" />
            <span>If this icon has changed to solid blue, someone has entered information into the Assumption box..</span>
          </div>
          <v-divider></v-divider>
          <div>
            <img class="small" src="../../assets/help/gl7.png" />
            <span>
              <ul>
                <li>Clicking this ellipsis will bring up a new menu of options.</li>
                <li>Add Flag or Comment will bring up the same screen as the first icon allowing you to add a comment, a flagged comment or resolve a comment</li>
                <li>Add Assumption or View Calculation will bring up the same screen as the center icon allowing you to add or edit your Assumption statement.</li>
                <li>Add Row will allow you to add a working row to this GL Account</li>
                <li>Delete Row Options will bring up a screen listing all the working rows for this GL Account and allowing you to delete one or more of these rows.</li>
                <li>Clear & Reset will clear out any data that has been added to this GL Account and reset the page back to the default values when the page was first loaded. This only applies to the specific tab you are on. It will also popup a message to explain what is about to happen and to allow you to confirm or cancel.</li>
              </ul>
            </span>
          </div>
          <br />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro GL Accounts')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'BudgetGeneralLedgerHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
